import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useRenameSession } from "@/api/mutations/sessions";
import type { Session } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: Session;
}

export function RenameSessionDialog({ open, onOpenChange, session }: Props) {
  const [title, setTitle] = useState(session.title);
  const [serverError, setServerError] = useState<string | null>(null);
  const mutation = useRenameSession();

  useEffect(() => {
    if (open) {
      setTitle(session.title);
      setServerError(null);
    }
  }, [open, session.title]);

  const trimmed = title.trim();
  const valid = trimmed.length > 0 && trimmed.length <= 200;

  function handleSubmit() {
    if (!valid) return;
    mutation.mutate(
      { agentId: session.agentId, sessionId: session.id, title: trimmed },
      {
        onSuccess: () => {
          toast.success("Session umbenannt");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Rename fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rename-session-dialog">
        <DialogHeader>
          <DialogTitle>Session umbenennen</DialogTitle>
          <DialogDescription>Titel max 200 Zeichen.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label htmlFor="session-title">Titel</Label>
          <Input
            id="session-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="rename-title-input"
            autoFocus
          />
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="rename-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rename-cancel">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!valid || mutation.isPending}
            data-testid="rename-submit"
          >
            {mutation.isPending ? "..." : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
